
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Trash, Image } from "lucide-react";
import { Product } from "@/types/services";

interface ProductFormProps {
  product: Product;
  index: number;
  userCurrency: string;
  handlePriceChange: (index: number, value: string) => void;
  handleImageUpload: (productIndex: number, files: FileList) => void;
  removeImage: (productIndex: number, imageIndex: number) => void;
  removeProduct: (index: number) => void;
  generateDescription: (index: number) => void;
  isGeneratingDescription: boolean;
  descriptionProductIndex: number | null;
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
}

export const ProductForm = ({
  product,
  index,
  userCurrency,
  handlePriceChange,
  handleImageUpload,
  removeImage,
  removeProduct,
  generateDescription,
  isGeneratingDescription,
  descriptionProductIndex,
  setProducts,
}: ProductFormProps) => {
  // Fixed handler for name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProducts(prev => {
      const newProducts = [...prev];
      newProducts[index] = {
        ...newProducts[index],
        name: e.target.value
      };
      return newProducts;
    });
  };

  // Fixed handler for description change
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setProducts(prev => {
      const newProducts = [...prev];
      newProducts[index] = {
        ...newProducts[index],
        description: e.target.value
      };
      return newProducts;
    });
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="space-y-4">
        <div>
          <Label htmlFor={`product-name-${index}`} className="flex items-center gap-2">
            <span>🔹</span> Service Name
          </Label>
          <Input
            id={`product-name-${index}`}
            value={product.name}
            onChange={handleNameChange}
            className="mt-1"
            placeholder="Enter service name"
            maxLength={50}
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-500">{product.name.length}/50</span>
          </div>
        </div>

        <div>
          <Label htmlFor={`product-price-${index}`} className="flex items-center gap-2">
            <span>��</span> Price ({userCurrency})
          </Label>
          <div className="relative mt-1">
            <Input
              id={`product-price-${index}`}
              type="text"
              inputMode="decimal"
              value={product.price === 0 ? '' : product.price.toString()}
              onChange={(e) => handlePriceChange(index, e.target.value)}
              className="pl-12"
              placeholder="0"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{userCurrency}</span>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center">
            <Label htmlFor={`product-description-${index}`} className="flex items-center gap-2">
              <span>📝</span> Description
            </Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generateDescription(index)}
              disabled={isGeneratingDescription}
              className="flex items-center gap-1.5 text-xs bg-gradient-to-r from-gebeya-pink/10 to-gebeya-orange/10 border-gebeya-pink/20 hover:bg-gradient-to-r hover:from-gebeya-pink/20 hover:to-gebeya-orange/20"
            >
              {isGeneratingDescription && descriptionProductIndex === index ? (
                <>
                  <img
                    src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png"
                    alt="Generating"
                    className="h-3.5 w-3.5 animate-spin"
                  />
                  <span className="text-gebeya-pink">Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5 text-gebeya-pink" />
                  <span className="text-gebeya-pink">Generate with AI</span>
                </>
              )}
            </Button>
          </div>
          <Textarea
            id={`product-description-${index}`}
            value={product.description}
            onChange={handleDescriptionChange}
            className="mt-1"
            placeholder="Describe your service (or use AI to generate a description)"
            rows={3}
          />
        </div>

        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <input
              type="file"
              id={`product-image-${index}`}
              className="hidden"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (e.target.files) {
                  handleImageUpload(index, e.target.files);
                }
              }}
            />
            <label
              htmlFor={`product-image-${index}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 cursor-pointer hover:bg-gray-50"
            >
              <Image className="w-4 h-4" />
              Upload images
            </label>
          </div>
          <button
            onClick={() => removeProduct(index)}
            className="p-2 text-gray-400 hover:text-red-500"
          >
            <Trash className="w-5 h-5" />
          </button>
        </div>

        {product.images.length > 0 && (
          <div className="mt-4 flex gap-4 flex-wrap">
            {product.images.map((image, imageIndex) => (
              <div key={imageIndex} className="relative">
                <img
                  src={image.preview}
                  alt={`Service preview ${imageIndex + 1}`}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <button
                  onClick={() => removeImage(index, imageIndex)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                >
                  <Trash className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
